import React from 'react';
import { Check, Download, Loader2 } from 'lucide-react';

const cx = (...parts) => parts.filter(Boolean).join(' ');

const GHOST_THEME = {
  blue: 'hover:bg-primary-50',
  purple: 'hover:bg-creative-50',
  emerald: 'hover:bg-emerald-50',
  orange: 'hover:bg-warning-50',
  pink: 'hover:bg-playful-50',
  gray: 'hover:bg-gray-50',
};

/**
 * ExpressiveIconButton (Ghost)
 * - Default background transparent
 * - Only shows bg-{theme}-50 on hover
 * - Touch target: min 48px (WCAG AA), visual icon ~18-20px
 */
export function ExpressiveIconButton({
  theme = 'gray',
  className,
  children,
  ...props
}) {
  return (
    <button
      type="button"
      className={cx(
        'min-h-touch-min min-w-touch-min px-sm',
        'inline-flex items-center justify-center rounded-lg',
        'bg-transparent text-text-secondary',
        'transition-colors',
        'hover:bg-gray-50 active:scale-95',
        GHOST_THEME[theme] || GHOST_THEME.gray,
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * LiquidDownloadButton
 * States: idle -> loading -> success
 * Uses CSS transitions for smooth icon swap (no extra deps).
 */
export function LiquidDownloadButton({
  state = 'idle', // 'idle' | 'loading' | 'success'
  theme = 'blue',
  className,
  ...props
}) {
  const isLoading = state === 'loading';
  const isSuccess = state === 'success';

  const hoverBg = GHOST_THEME[theme] || GHOST_THEME.blue;

  return (
    <button
      type="button"
      className={cx(
        'min-h-touch-min min-w-touch-min px-sm',
        'inline-flex items-center justify-center rounded-lg',
        'bg-transparent',
        'transition-colors',
        'hover:bg-gray-50 active:scale-95',
        hoverBg,
        className
      )}
      aria-busy={isLoading ? 'true' : 'false'}
      {...props}
    >
        <span className="relative w-5 h-5">
        <span
          className={cx(
            'absolute inset-0 flex items-center justify-center',
            'transition-all duration-200',
            (!isLoading && !isSuccess) ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
          )}
        >
          <Download size={20} className="w-5 h-5" />
        </span>
        <span
          className={cx(
            'absolute inset-0 flex items-center justify-center',
            'transition-all duration-200',
            isLoading ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
          )}
        >
          <Loader2 size={20} className="w-5 h-5 animate-spin" />
        </span>
        <span
          className={cx(
            'absolute inset-0 flex items-center justify-center',
            'transition-all duration-200',
            isSuccess ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
          )}
        >
          <Check size={20} className="w-5 h-5" />
        </span>
      </span>
    </button>
  );
}

/**
 * ExpressivePillButton (Ghost)
 * - Icon + label
 * - Rounded full ("pill")
 * - Touch target: min 44px
 */
export function ExpressivePillButton({
  theme = 'gray',
  className,
  icon,
  children,
  ...props
}) {
  return (
    <button
      type="button"
      className={cx(
        'min-h-touch-min px-md',
        'inline-flex items-center justify-center gap-sm',
        'rounded-full',
        'bg-transparent text-text-primary',
        'transition-colors',
        'hover:bg-gray-50 active:scale-95',
        GHOST_THEME[theme] || GHOST_THEME.gray,
        className
      )}
      {...props}
    >
      {icon ? <span className="w-5 h-5 flex items-center justify-center">{icon}</span> : null}
      <span className="text-mobile-body-sm font-bold">{children}</span>
    </button>
  );
}


